export const enSalesforce = {
	title: "Salesforce Integration",
	connected: "Connected",
	notConnected: "Not Connected",
	connect: "Connect",
	disconnect: "Disconnect",
	configureFieldMapping: "Configure Field Mapping",
	fieldMapping: {
		title: "Salesforce Field Mapping",
		contacts: "Contacts",
		accounts: "Accounts",
		leads: "Leads",
		opportunities: "Opportunities",
		ourField: "Signalist Field",
		salesforceField: "Salesforce Field",
		active: "Active",
		actions: "Actions",
		addMapping: "Add Mapping",
		restoreDefaults: "Restore default mappings",
		restoreDefaultsTooltip:
			"Adds the standard Salesforce mappings you are missing on this tab. Existing mappings stay untouched.",
		restoreDefaultsNoneAdded: "All default mappings are already configured.",
		restoreDefaultsFailed: "The default mappings could not be restored.",
		saveAll: "Save All",
		selectField: "Select a field",
		noMappings: "No field mappings configured yet.",
		deleteConfirm: "Are you sure you want to delete this mapping?",
		sourceTypeFromRecord: "From record",
		sourceTypeConstant: "Constant value",
		sourceTypeAi: "AI-generated",
		constantPlaceholder: "Enter a literal value",
		constantLabel: 'Constant: "{{value}}"',
		aiBadge: "AI",
		aiConfigure: "Configure",
		aiTargetPlaceholder: "Pick a Salesforce field →",
		aiCreditTooltip: "0.1 credits per lead synced — flat, covers all AI fields",
		aiHintTitle: "AI-enriched fields",
		aiHintBody:
			"Let an AI agent classify, research, or extract a value before syncing. AI enrichment costs 0.1 credits per lead — flat, regardless of how many AI fields you add. The field name will match your selected Salesforce field.",
		aiSaveDisabledTooltip: "Configure AI to save",
		cannotMap: "— Cannot map —",
		cannotMapTooltip: "Salesforce computes this field — Signalist cannot write to it",
		/**
		 * Opportunity only. A reserved field is perfectly writable — it simply loses to the deal
		 * configuration, which fills it after the coercion pass. Saying "read-only" here would be
		 * wrong and would send the customer looking for a permission problem that does not exist.
		 */
		reservedTooltip:
			"The opportunity configuration owns this field — set it under Opportunity Configuration instead.",
		metaBadge: {
			required: "Required",
			multi: "Multi",
			dropdown: "Picklist",
			number: "Number",
			calculated: "Formula",
			readOnly: "Read-only",
			restricted: "Restricted",
		},
		footerSummary: "{{mapped}} fields mapped · {{ai}} AI-enriched",
		footerCreditPill: "+0.1 credits per lead with AI enrichment",
		preview: {
			button: "Test with a lead",
			title: "Test field mapping",
			subtitle:
				"Pick a lead and see exactly which values a sync would write to Salesforce, checked against your org's field metadata. Nothing is written — this is a dry run. AI-enriched fields are not executed here; use their test panel instead.",
			cancel: "Cancel",
			createsLabel: "A sync will create",
			createsByEntity: {
				CONTACT: "A sync creates or updates a contact (and its account) in Salesforce.",
				ACCOUNT: "A sync creates or updates an account (and its contact) in Salesforce.",
				LEAD: "A sync creates a lead in Salesforce.",
				OPPORTUNITY: "A sync creates an opportunity in Salesforce.",
			},
			prospectLabel: "Lead",
			prospectPlaceholder: "Search leads by name…",
			run: "Run dry run",
			value: "Value",
			emptyValue: "Empty for this lead",
			aiNotRun: "AI — not executed in the dry run",
			inactive: "Inactive",
			/**
			 * The preview prints the value *after* coercion, because the Salesforce coercion pass
			 * changes values (text truncated to the field's length, dates to YYYY-MM-DD,
			 * multi-picklists re-joined on `;`). The raw value only shows next to a drop reason.
			 */
			coercedHint: "Values are shown exactly as the sync would write them, after coercion.",
			rawValueLabel: "Original value",
			fallbacksTitle: "Filled in automatically",
			fallbacksHint:
				"Salesforce needs these fields; the sync fills them when no active mapping supplies a value.",
			syncResolvedTitle: "Resolved by the sync",
			syncResolvedHint:
				"The sync sets these from ids a dry run does not have yet (the account it just wrote, the resolved owner, the opportunity record type). They are not missing.",
			noMappings: "No mappings on this tab.",
			error: "The test failed. Please try again.",
			warnings: {
				missingRequiredTitle: "Missing required fields",
				missingRequiredHint:
					"Salesforce marks these fields required, but neither an active mapping nor an AI field nor an automatic fallback supplies a value for this lead. A sync would be rejected:",
			},
			sync: {
				button: "Sync this lead now",
				warningByEntity: {
					CONTACT:
						"End-to-end check: runs a real sync for this lead and shows Salesforce's answer. The contact and its account are created or updated (AI fields run and are charged as usual).",
					ACCOUNT:
						"End-to-end check: runs a real sync for this lead and shows Salesforce's answer. The account and its contact are created or updated (AI fields run and are charged as usual).",
					LEAD: "End-to-end check: runs a real sync and shows Salesforce's answer. A NEW lead is created every time you press this — delete the test records afterwards. AI fields run and are charged as usual.",
					OPPORTUNITY:
						"End-to-end check: runs a real sync and shows Salesforce's answer. A NEW opportunity is created every time you press this — delete the test records afterwards. AI fields run and are charged as usual.",
				},
				successTitle: "Salesforce accepted the record — your mapping works.",
				failedTitle: "Salesforce rejected the record:",
				openRecord: "Open in Salesforce",
				contactRecord: "Contact {{id}}",
				accountRecord: "Account {{id}}",
				leadRecord: "Lead {{id}}",
				opportunityRecord: "Opportunity {{id}}",
				duplicateHint:
					"Your org's duplicate rules rejected this record. Salesforce found an existing match — merge or adjust the rule in Setup → Duplicate Rules.",
			},
		},
		aiConfigTitle: "Configure AI-enriched field",
		aiConfigSubtitle: "Target: {{label}}",
		aiConfig: {
			configurationTitle: "Configuration",
			promptLabel: "Prompt",
			promptPlaceholder: "Tell the AI how to generate this CRM field value...",
			promptHelp:
				"The AI runs once per sync with this prompt and the prospect and organization data as input.",
			promptMeta: "Editable · pre-filled from field metadata",
			defaultPrompt:
				"Generate a value for {{target}}. Use the available prospect and organization context. Allowed values: {{allowedValues}}. Return only the final value.",
			insertVariable: "Insert variable",
			variablesLabel: "Variables",
			allowedValuesTitle: "Allowed values",
			allowedValuesEmpty: "No allowed values returned for this field.",
			fallbackLabel: "Fallback value",
			fallbackHelp:
				"Used when the AI cannot determine a confident value. For a restricted picklist the fallback must be one of the allowed values above — Salesforce rejects anything else.",
			leaveEmpty: "Leave empty",
			behaviorTitle: "Behavior",
			syncReasoningLabel: "Sync reasoning",
			syncReasoningHelp: "Write the AI reasoning to another text field.",
			syncReasoningTargetLabel: "Sync reasoning to",
			syncReasoningTargetHelp:
				"Only text fields can receive reasoning. A Long Text Area is strongly preferred — a short Text field truncates it.",
			syncSourceLabel: "Sync source",
			syncSourceHelp: "Write the source list to another text field.",
			syncSourceTargetLabel: "Sync source to",
			syncSourceTargetHelp: "Only text fields can receive sources.",
			secondaryTargetPlaceholder: "Select a text field",
			testTitle: "Test",
			freeDuringSetup: "Free during setup",
			testIntroTitle: "Test this prompt with a prospect from your pipeline",
			testIntroHelp:
				"Load the variables for a prospect, adjust values if needed, and run a free test.",
			prospectLabel: "Prospect",
			prospectPlaceholder: "Search prospects...",
			loadVariables: "Load variables",
			variablesPreviewTitle: "Variables preview",
			noVariables: "Pick a prospect and load variables to preview the prompt data.",
			editData: "Edit data",
			revertEdits: "Revert edits",
			runTest: "Run test",
			rerunTest: "Re-run test",
			testRunHelp: "Test runs are free, do not update the CRM, and do not consume credits.",
			progressLabel: "Load lead data → analyze & match → return result",
			loadingLeadData: "Load prospect data",
			loadingResearch: "Research organization",
			loadingAnalyze: "Analyze & match",
			loadingReturn: "Return result",
			resultTitle: "Result",
			resultReadyTitle: "AI returned a value",
			valueLabel: "Value",
			reasoningLabel: "Reasoning",
			sourcesLabel: "Sources",
			durationLabel: "Duration",
			fallbackUsed: "Fallback was used.",
			finalPrompt: "Final prompt",
			emptyState: "No test run yet.",
			noCredits: "Tests do not consume credits and do not write to the CRM.",
			costReminder: "0.1 credits per lead synced (flat — covers all AI fields)",
			cancel: "Cancel",
			save: "Save AI mapping",
			requiredPrompt: "Prompt is required",
			requiredSecondaryField: "Select a text field",
			invalidFallback: "Fallback must be one of the allowed values",
			testError: "AI test failed. Please try again.",
		},
	},
	tabs: {
		connection: "Connection",
		settings: "Settings",
		fieldMapping: "Field Mapping",
		dealConfiguration: "Opportunity Configuration",
		leadConfiguration: "Lead Configuration",
		userMapping: "User Mapping",
		syncLogs: "Sync Logs",
	},
	syncTrigger: {
		title: "Sync Prospects to Salesforce",
		description: "Choose when prospects should be automatically synchronized to Salesforce.",
		DISABLED: "Disabled",
		ON_CAMPAIGN_START: "When campaign starts",
		ON_REPLY: "When prospect replies",
		INHERIT: "Use company default",
		inheritedFrom: "Currently using company setting",
		error: "Failed to update the sync trigger.",
	},
	dealCreationTrigger: {
		title: "Automatic Opportunity Creation",
		description:
			"Choose when opportunities or leads should be automatically created in Salesforce for prospects that already exist. This setting applies to your whole account — Salesforce has no per-list override.",
		DISABLED: "Off",
		ON_RESPONSE: "On Response",
		ON_POSITIVE_RESPONSE: "On Positive Response",
		createAsLead: "Create as Lead instead of Opportunity",
		createAsLeadDescription:
			"When enabled, automatic creation produces a Salesforce Lead instead of an Opportunity.",
		error: "Failed to update the opportunity-creation trigger.",
	},
	dealConfig: {
		title: "Opportunity Defaults",
		description: "Configure default values for new Salesforce opportunities.",
		defaultPipeline: "Default Record Type",
		defaultStage: "Default Stage",
		/**
		 * Salesforce has no pipeline object at all. What the picker calls a pipeline is an
		 * Opportunity record type, and orgs frequently have none — the backend then synthesises
		 * one pipeline over the whole active StageName picklist.
		 */
		pipelineHelp:
			"Salesforce has no pipelines. A record type selects the sales process, and therefore which stages an opportunity may use.",
		defaultPipelineNote:
			"Your org has no Opportunity record types, so stages come from the standard Stage picklist.",
		dealNameTemplate: "Opportunity Name Template",
		dealNameTemplateHelp:
			"Use {firstName}, {lastName}, {company}, {email}, {title} as placeholders.",
		insertPlaceholder: "Insert placeholder",
		closeDateOffsetDays: "Close Date Offset (days)",
		closeDateOffsetHelp: "Number of days from today used for the close date (0-3650).",
		defaultAmount: "Default Amount",
		defaultAmountHelp:
			"Written to the opportunity's Amount. Leave empty to create opportunities without an amount.",
		defaultOwner: "Default Opportunity Owner",
		saved: "Opportunity configuration saved successfully.",
		saveError: "Failed to save opportunity configuration.",
	},
	leadConfig: {
		title: "Lead Defaults",
		description:
			"Configure default values applied to new Salesforce Leads. Applies when the opportunity-creation trigger has 'Create as Lead' enabled, and when manually creating a Lead from a prospect.",
		leadTopicTemplate: "Lead Company Template",
		leadTopicTemplateHelp:
			"Fills the lead's Company, which Salesforce requires. Use {firstName}, {lastName}, {company}, {email}, {title} as placeholders. Left empty, the prospect's organization name is used.",
		leadTopicTemplatePlaceholder: "Leave empty to use the organization name",
		defaultOwner: "Default Lead Owner",
		defaultLeadStatus: "Default Lead Status",
		defaultLeadStatusHelp:
			"Written to the lead's Status when no active LEAD mapping supplies one. A mapping onto Status always wins.",
		estimatedValue: "Estimated Value",
		/**
		 * Deliberately explicit: the column exists and round-trips, but a standard Salesforce Lead
		 * has no currency field, so nothing is written. Ticket 6's correction, surfaced to the user
		 * rather than left as a silent no-op.
		 */
		estimatedValueHelp:
			"Stored for your reference only. A standard Salesforce Lead has no amount field, so this is not written — map a LEAD field onto your own currency field to sync it.",
		saved: "Lead configuration saved successfully.",
		saveError: "Failed to save lead configuration.",
	},
	userMapping: {
		title: "User Mapping",
		description:
			"Map Signalist users to Salesforce users for record ownership. A task written without an owner belongs to the connected user, so leaving a sender unmapped puts their conversation on the wrong timeline.",
		signalistUser: "Signalist User",
		salesforceOwner: "Salesforce User",
		selectOwner: "Select a user",
		noUsers: "No sending users found.",
		saved: "User mappings saved successfully.",
		saveError: "Failed to save user mappings.",
	},
	createDeal: {
		titleDeal: "Create Salesforce Opportunity",
		titleLead: "Create Salesforce Lead",
		submitDeal: "Create Opportunity",
		submitLead: "Create Lead",
		createAsLead: "Create as Lead",
		createAsLeadHelp: "A Salesforce Lead carries the person and the company on one record.",
		dealName: "Opportunity Name",
		leadTopic: "Lead Company",
		defaultPipeline: "Record Type",
		defaultStage: "Stage",
		defaultPipelineNote:
			"Your org has no Opportunity record types, so stages come from the standard Stage picklist.",
		owner: "Owner",
		closeDate: "Close Date",
		amount: "Amount",
		success: "Created in Salesforce.",
		error: "Failed to create the record in Salesforce.",
	},
	conversationSync: {
		title: "Conversation Sync",
		label: "Sync conversations to Salesforce",
		description:
			"When enabled, messages sent to and received from prospects are mirrored as Tasks on the matching Salesforce contact or lead.",
		success: "Conversation sync setting updated successfully.",
		error: "Failed to update conversation sync setting.",
	},
	oauth: {
		connectWithSalesforce: "Connect with Salesforce",
		connectedViaOAuth: "Connected via Salesforce OAuth",
		/**
		 * The environment picker has no Dynamics equivalent. Salesforce scopes a refresh token to
		 * the host that issued it, so the choice is made before the redirect and replayed on every
		 * refresh — refreshing a sandbox token against login.salesforce.com fails outright.
		 */
		environment: "Salesforce Environment",
		environmentDescription:
			"Salesforce signs you in on a different host per environment, and a connection stays tied to the host it was created on. Pick the one your org uses before connecting.",
		environmentProduction: "Production",
		environmentProductionHint: "The standard production login.",
		environmentSandbox: "Sandbox",
		environmentSandboxHint: "For a sandbox or a Developer Edition org.",
		environmentMyDomain: "My Domain",
		environmentMyDomainHint: "Your org's own login host, if My Domain login is enforced.",
		myDomainLabel: "My Domain URL",
		myDomainPlaceholder: "https://acme.my.salesforce.com",
		myDomainHelp:
			"Find it in Setup → Company Settings → My Domain. Enter the origin only — no path, port or query.",
		myDomainInvalid:
			"Enter a My Domain origin, for example https://acme.my.salesforce.com (https only, no path or port)",
		myDomainRequired: "Enter your My Domain URL to continue.",
		instanceUrl: "Instance",
		loginHost: "Login host",
		salesforceOrgId: "Organization id",
		salesforceUserId: "Connected user",
		tokenRefresh: "Token refreshes automatically",
		disconnectTitle: "Disconnect Salesforce?",
		disconnectBody: "This will remove the connection. Salesforce syncing will stop.",
		connectError: "Failed to generate the authorization URL",
		alreadyDisconnected: "Salesforce is already disconnected.",
		disconnectError: "Failed to disconnect from Salesforce.",
	},
	syncLogs: {
		description: "View the history of data synchronization between Signalist and Salesforce.",
		filterByEntity: "Filter by Entity",
		allEntities: "All Entities",
		entityType: "Entity Type",
		status: "Status",
		synced: "Synced",
		failed: "Failed",
		error: "Error",
		errorCode: "Salesforce code",
		errorFields: "Fields",
		startedAt: "Started At",
		completedAt: "Completed At",
		noLogs: "No sync logs found.",
	},
	resync: {
		title: "Resync with Salesforce",
		description:
			"Re-push every already-synced prospect to Salesforce using your current field mapping. Useful after you add or change mapped fields.",
		button: "Resync now",
		confirmTitle: "Resync with Salesforce?",
		confirmBody:
			"This will re-push every prospect that is already synced to Salesforce using your current field mapping. AI-enriched fields run again and are charged as usual. Continue?",
		confirmCta: "Resync",
		success: "Re-syncing {{count}} contact(s) to Salesforce. Check the sync log for progress.",
		empty: "No already-synced prospects found.",
		error: "Failed to queue resync. Please try again.",
	},
	callback: {
		successTitle: "Salesforce connected",
		successBody: "You can close this tab and return to Signalist.",
		errorTitle: "Salesforce connection failed",
		backToIntegrations: "Back to integrations",
	},
};
